import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { homePathFor } from '../lib/roles';
import clsx from 'clsx';
import {
  LayoutDashboard,
  FilePlus,
  Users,
  BarChart3,
  ScanLine,
  Search,
  ClipboardList,
  FileText,
  UserCog,
  Pill,
  TrendingUp,
  Activity,
  LogOut,
  Languages,
  Menu,
  X,
  CreditCard,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { SubscriptionBanner } from '../features/pharmacist/SubscriptionBanner';
import { ErrorBoundary } from '../components/ui';

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

const navItemsByRole: Record<string, NavItem[]> = {
  DOCTOR: [
    { label: 'nav.dashboard', path: '/doctor', icon: LayoutDashboard },
    { label: 'nav.newPrescription', path: '/doctor/prescriptions/new', icon: FilePlus },
    { label: 'nav.prescriptions', path: '/doctor/prescriptions', icon: FileText },
    { label: 'nav.patients', path: '/doctor/patients', icon: Users },
    { label: 'nav.reports', path: '/doctor/reports', icon: BarChart3 },
  ],
  PHARMACIST: [
    { label: 'nav.dashboard', path: '/pharmacist', icon: LayoutDashboard },
    { label: 'nav.scanQR', path: '/pharmacist/scan', icon: ScanLine },
    { label: 'nav.search', path: '/pharmacist/search', icon: Search },
    { label: 'nav.history', path: '/pharmacist/history', icon: ClipboardList },
    { label: 'nav.reports', path: '/pharmacist/reports', icon: BarChart3 },
    { label: 'nav.subscription', path: '/pharmacist/subscription', icon: CreditCard },
  ],
  PATIENT: [{ label: 'nav.myPrescriptions', path: '/patient', icon: FileText }],
  ADMIN: [
    { label: 'nav.dashboard', path: '/admin', icon: LayoutDashboard },
    { label: 'nav.users', path: '/admin/users', icon: UserCog },
    { label: 'nav.drugs', path: '/admin/drugs', icon: Pill },
    { label: 'nav.audit', path: '/admin/audit', icon: ClipboardList },
    { label: 'nav.reports', path: '/admin/reports', icon: TrendingUp },
    { label: 'nav.systemHealth', path: '/admin/health', icon: Activity },
  ],
  AUDITOR: [
    { label: 'nav.dashboard', path: '/admin', icon: LayoutDashboard },
    { label: 'nav.audit', path: '/admin/audit', icon: ClipboardList },
    { label: 'nav.reports', path: '/admin/reports', icon: TrendingUp },
  ],
};

const roleRootPaths = new Set(['/doctor', '/pharmacist', '/patient', '/admin']);

export function DashboardLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the mobile drawer on navigation and on Escape.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSidebarOpen(false);
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    // Drop cached data so the next user never sees the previous user's records.
    queryClient.clear();
    navigate('/login');
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'ar' ? 'fr' : 'ar');
  };

  const navItems = user ? navItemsByRole[user.role] || [] : [];
  const homePath = homePathFor(user?.role);
  const initials = `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}`.toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm transition-opacity lg:hidden',
          sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={clsx(
          'print:hidden fixed inset-y-0 start-0 z-50 flex w-64 flex-col border-e border-gray-200 bg-white transition-transform duration-200 lg:translate-x-0 lg:rtl:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-100 px-5">
          <Link to={homePath} className="flex items-center">
            <img src="/images/pharmatec-logo.png" alt="PharmaTec" className="h-9" />
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="btn-icon lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-5">
          {user?.role && (
            <p className="section-title mb-2 px-3">{t(`roles.${user.role.toLowerCase()}`)}</p>
          )}
          <ul className="space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={roleRootPaths.has(item.path) || item.path === '/doctor/prescriptions'}
                    className={({ isActive }) =>
                      clsx(
                        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-800'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute inset-y-1.5 start-0 w-0.5 rounded-full bg-primary-600" />
                        )}
                        <Icon
                          className={clsx(
                            'h-[18px] w-[18px] shrink-0',
                            isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                          )}
                        />
                        <span className="truncate">{t(item.label)}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info */}
        <div className="shrink-0 border-t border-gray-100 p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy-900 text-xs font-semibold text-white">
              {initials || '?'}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>

          {user?.mfaEnabled === false && (
            <Link
              to="/mfa-setup"
              className="mb-1 flex items-center gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs font-medium text-warning-800 ring-1 ring-inset ring-warning-600/20 hover:bg-warning-100"
            >
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <span className="truncate">{t('auth.mfa.setup')}</span>
            </Link>
          )}

          <div className="mt-1 grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <Languages className="h-4 w-4" />
              {i18n.language === 'ar' ? 'Français' : 'العربية'}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs font-medium text-danger-600 hover:bg-danger-50"
            >
              <LogOut className="h-4 w-4 rtl-flip" />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ps-64 print:ps-0">
        {/* Mobile top bar */}
        <header className="print:hidden sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-gray-200 bg-white/90 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="btn-icon -ms-2"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to={homePath}>
            <img src="/images/pharmatec-logo.png" alt="PharmaTec" className="h-7" />
          </Link>
        </header>

        <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {user?.role === 'PHARMACIST' && <SubscriptionBanner />}
          {/* keyed by path so navigating away from a crashed page recovers */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
